import { isString, get, omitBy, isNil, isNull, map, reject } from 'lodash-es';
import moment from 'moment';
import type { CoreTypes } from '../../core';
import type { CalendarEvent } from '../types/calendar-events';
import { getDivision, getDivisionId, hasDivisionId, toReference, type DivisionScope } from './common';
import toActivities from './activities';
import toGroup from './groups';
import toTeacher from './duties';

type Expand = ('referenceNames' | 'teachers' | 'activity' | 'groups')[];

export default (
  events:       Extract<CoreTypes.Mixed.Event, { id: string }>[],
  domain?:      DivisionScope,
  expand?:      Expand
): Partial<CalendarEvent>[] => {
  if (!Array.isArray(events))
    throw new Error('(SS12000::To::CalendarEvents) CalendarEvent input is not of array type', { cause: 'invalid_input' });

  if (!events.every((event) => isString(event) || isString(get(event, 'id'))))
    throw new Error('(SS12000::To::CalendarEvents) Some CalendarEvents are missing "id" values', { cause: 'invalid_input' });

  if (!domain && !events.every((event) => !isString(event) && hasDivisionId(event)))
    throw new Error('(SS12000::To::CalendarEvents) Some top layer calendar events are missing division values', { cause: 'invalid_input' });

  return events.map((event) => {
    const division = getDivision(event, domain);
    if (!division || isString(division) || !('id' in division && division.id))
      throw new Error('(SS12000::To::CalendarEvents) Some events members are missing division values', { cause: 'invalid_input' });
    if (!('start' in division && division.start) || !('end' in division && division.end))
      throw new Error('(SS12000::To::CalendarEvents) Some events members are missing division start or end values', { cause: 'invalid_input' });

    const course = (!isString(event) && event.course && !isString(event.course)) ?
      event.course as Extract<CoreTypes.Mixed.Course, { id: string }>
      : undefined;

    return {
      id: `${ getDivisionId(event, domain) }.${ isString(event) ? event : event.id }`,
      ...!isString(event) && {
        meta: {
          created:  moment(event.createdAt).toISOString(),
          modified: moment(event.updatedAt).toISOString(),
          ...event.type          && { type: event.type },
        },
        ...omitBy({
          foreignId: event.ids,
          color:     event.color ?? course?.color,
        }, isNil),
        startTime: event.start?.toString(),
        endTime:   event.end?.toString(),
        // Locked times have no course, so we need to check for it
            // @ts-ignore
        ...course && { activity: toReference([course], division, expand?.includes('referenceNames'))[0] },
            // @ts-ignore
        rooms:     toReference(reject(event.inLocations, isNull) ?? [], division, expand?.includes('referenceNames')),
            // @ts-ignore
        resources: toReference([], division, expand?.includes('referenceNames')),
            // @ts-ignore
        teachers:  toReference(map(event.teachers ?? course?.teachers ?? [], 'to'), division, expand?.includes('referenceNames')),
            // @ts-ignore
        groups:    toReference(map(event.groups ?? course?.groups ?? [], 'to'), division, expand?.includes('referenceNames')),
        ...expand?.some(x => ['groups', 'teachers', 'activity'].includes(x)) && {
          _embedded: {
            ...expand.includes('groups') && {
            // @ts-ignore
              groups: toGroup(map(event.groups, 'to'), division)
            },
            ...expand.includes('teachers') && {
            // @ts-ignore
              teachers: toTeacher(map(event.teachers, 'to'), division)
            },
            ...(expand.includes('activity') && course)&& {
              activity: toActivities([course], division as Extract<CoreTypes.Deep.Division, { id: string }>)[0]
            }
          }
        }
      }
    }
  });
};
